import React from "react";
import { DocumentNew } from "models/schemas";
import { Box, SpaceBetween } from "@amzn/awsui-components-react";
import { SearchDocumentRequest, SearchDocumentResponse } from "./definitions";

export interface VersionCollisionInfoOS {
  hasCollision: boolean;
  fileName: string;
  wouldBecomeVersion: number;
  totalVersionsAfter: number;
  existingDoc?: DocumentNew;
  sourceVersionCount?: number;
}

/**
 * Creates a "no collision" response
 */
const createNoCollisionResult = (fileName: string): VersionCollisionInfoOS => ({
  hasCollision: false,
  fileName,
  wouldBecomeVersion: 1,
  totalVersionsAfter: 1,
});

/**
 * Checks if updating a document would create a version collision by querying OpenSearch
 * Version key = fileName.extensionType|documentType|parentId
 *
 * @param document The document being checked
 * @param newProperties The properties being changed
 * @param searchDocuments Function to search documents in OpenSearch
 * @returns Collision information
 */
export const checkVersionCollisionOS = async (
  document: DocumentNew,
  newProperties: Partial<DocumentNew>,
  searchDocuments: (
    params: SearchDocumentRequest
  ) => Promise<SearchDocumentResponse>
): Promise<VersionCollisionInfoOS> => {
  const isNewDocument =
    !newProperties || Object.keys(newProperties).length === 0;

  const updatedDoc = { ...document, ...newProperties };

  // Build version key components
  const newVersionKeyComponents = {
    fileName: updatedDoc.fileName,
    extensionType: updatedDoc.extensionType,
    documentType: updatedDoc.documentType,
    parentId: updatedDoc.parentId,
  };

  if (!isNewDocument) {
    const currentVersionKeyComponents = {
      fileName: document.fileName,
      extensionType: document.extensionType,
      documentType: document.documentType,
      parentId: document.parentId,
    };

    // No collision if the version key hasn't changed
    if (
      JSON.stringify(newVersionKeyComponents) ===
      JSON.stringify(currentVersionKeyComponents)
    ) {
      return createNoCollisionResult(document.fileName);
    }
  }

  try {
    // Query OpenSearch for documents matching the new version key
    const result = await searchDocuments({
      teamName: document.teamName,
      propertyFilterQuery: {
        operation: "and",
        tokens: [
          {
            propertyKey: "fileName",
            operator: "=",
            value: newVersionKeyComponents.fileName,
          },
          {
            propertyKey: "extensionType",
            operator: "=",
            value: newVersionKeyComponents.extensionType,
          },
          {
            propertyKey: "documentType",
            operator: "=",
            value: newVersionKeyComponents.documentType,
          },
          {
            propertyKey: "parentId",
            operator: "=",
            value: newVersionKeyComponents.parentId,
          },
        ],
      },
      pageSize: 1,
    });

    // Check if a different document exists with these properties
    if (
      result.documents.length > 0 &&
      result.documents[0].documentId !== document.documentId
    ) {
      const existingDoc = result.documents[0];
      const existingVersionCount = (existingDoc.versions?.length || 0) + 1; // +1 for current
      const sourceVersionCount = (document.versions?.length || 0) + 1; // +1 for current

      // Calculate what version number this document would become
      // The current version has the highest number (newest = total count)
      const totalVersionsAfter = existingVersionCount + sourceVersionCount;
      const wouldBecomeVersion = totalVersionsAfter; // This document becomes the newest version

      return {
        hasCollision: true,
        fileName: document.fileName,
        wouldBecomeVersion,
        totalVersionsAfter,
        existingDoc,
        sourceVersionCount,
      };
    }

    return createNoCollisionResult(document.fileName);
  } catch (error) {
    console.error("Error checking version collision:", error);
    // If there's an error, assume no collision to allow operation to proceed
    return createNoCollisionResult(document.fileName);
  }
};

/**
 * Checks multiple documents for version collisions
 */
export const checkBatchVersionCollisionsOS = async (
  documents: DocumentNew[],
  getNewProperties: (doc: DocumentNew) => Partial<DocumentNew>,
  searchDocuments: (
    params: SearchDocumentRequest
  ) => Promise<SearchDocumentResponse>
): Promise<VersionCollisionInfoOS[]> => {
  const collisions: VersionCollisionInfoOS[] = [];

  for (const doc of documents) {
    const newProps = getNewProperties(doc);
    const collision = await checkVersionCollisionOS(
      doc,
      newProps,
      searchDocuments
    );

    if (collision.hasCollision) {
      collisions.push(collision);
    }
  }

  return collisions;
};

/**
 * Renders a React element displaying version collision warning message
 */
export const renderVersionCollisionMessageOS = (
  collisions: VersionCollisionInfoOS[]
): React.ReactElement => {
  if (collisions.length === 1) {
    const collision = collisions[0];
    const hasMultipleVersions = (collision.sourceVersionCount || 1) > 1;
    const olderVersionCount = (collision.sourceVersionCount || 1) - 1;

    return (
      <Box>
        {`Proceeding with this action will make this file${
          hasMultipleVersions
            ? ` and its ${olderVersionCount} existing version${
                olderVersionCount > 1 ? "s" : ""
              }`
            : ""
        } a version of an existing file.${
          hasMultipleVersions ? " The current version of" : ""
        } `}
        <b>{collision.fileName}</b>
        {` will become version `}
        <b>{collision.wouldBecomeVersion}</b>
        {` of `}
        <b>{collision.totalVersionsAfter}</b>
        {` total versions.`}
      </Box>
    );
  }

  const hasAnyMultipleVersions = collisions.some(
    (c) => (c.sourceVersionCount || 1) > 1
  );

  return (
    <SpaceBetween direction="vertical" size="s">
      <Box>
        {`Proceeding with this action will make these files${
          hasAnyMultipleVersions ? " and their existing versions" : ""
        } versions of existing files:`}
      </Box>
      <ul style={{ marginLeft: "20px" }}>
        {collisions.map((collision, index) => {
          const hasMultipleVersions = (collision.sourceVersionCount || 1) > 1;
          const olderVersionCount = (collision.sourceVersionCount || 1) - 1;

          return (
            <li key={index}>
              {hasMultipleVersions && <>{`The current version of `}</>}
              <b>{collision.fileName}</b>
              {hasMultipleVersions && (
                <>
                  {` (with ${olderVersionCount} existing version${
                    olderVersionCount > 1 ? "s" : ""
                  })`}
                </>
              )}
              {` will become version `}
              <b>{collision.wouldBecomeVersion}</b>
              {` of `}
              <b>{collision.totalVersionsAfter}</b>
              {` total versions.`}
            </li>
          );
        })}
      </ul>
    </SpaceBetween>
  );
};
